import * as React from 'react'
import { useLocation } from 'react-router-dom'

import * as Dialog from '@radix-ui/react-dialog'
import isEqual from 'lodash/isEqual'
import { AnimatePresence } from 'motion/react'

import {
  Flex,
  useControllableState,
  VisuallyHidden,
  useMergeRefs,
  UnstyledButton,
  XIcon,
} from '@buffer-mono/popcorn'
import type { Uploader } from '@buffer-mono/uploader'

import {
  createIdeaEditor,
  IdeaEditor,
} from '~publish/components/LegacyIdeaComposer/IdeaEditor'
import {
  useAccountEmail,
  useAccountId,
  useOrganizationLimits,
} from '~publish/contexts/AccountContext'
import type { SelectedTag } from '~publish/legacy/tags/types'
import { SlateJsProxy } from '~publish/legacy/composer/composer/entities/EditorStateProxy'
import { getPlainText } from '~publish/legacy/editor/BufferEditor'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'
import { useAppDispatch, useAppSelector } from '~publish/legacy/store'
import { selectIncludedMedia } from '~publish/legacy/uploads/state/selectors'
import { preloadMedia } from '~publish/legacy/uploads/state/thunks/preloadMedia'
import { UNASSIGNED_GROUP_ID } from '~publish/pages/Create/components/Board/helpers/helpers'
import { useIdeasCount } from '~publish/pages/Create/hooks/useIdeasCount'
import type { Idea, IdeaContent } from '~publish/pages/Create/types'

import { AIAssistantIdeasSidePanel } from './AIAssistantSidePanel'
import { ConfirmCloseModal } from './ConfirmCloseModal'
import { IdeaComposerPanel } from './IdeaComposerPanel'
import { IDEAS_UPLOADER_ID } from './constants'
import { isExistingIdea } from './helpers'
import { IdeasLimitReachedModal } from './IdeasLimitReachedModal'
import { useSaveIdea } from './useSaveIdea'
import type { SaveIdeaError } from './useSaveIdea'
import { createPostFromIdea as createPostFromIdeaThunk } from '../../pages/Create/state/thunks/createPostFromIdea' // Still not sure where this thunk should live so keeping in Create page for now
import { isIdeaComposerLocationState } from '../IdeaManagementRouter/hooks'

import styles from './IdeaComposer.module.css'

import type { JSX } from 'react'

export type NewIdea = {
  id?: string
  content: Omit<IdeaContent, 'aiAssisted'>
  groupId?: Idea['groupId']
}

export type DraftIdea = NewIdea | Idea

type IdeaComposerContentProps = {
  onClose: () => void
  uploader: Uploader
}

/**
 * Internal component responsible for managing dialog interactions and overall Dialog composition.
 * - Dialog portal rendering and positioning
 * - Overlay click handling and close confirmations
 * - Conditional rendering of composer content or paywall
 * - Rendering of side panels
 *
 * @private
 */
const IdeaComposerContent = React.forwardRef<
  HTMLDivElement,
  IdeaComposerContentProps
>((props, ref): JSX.Element => {
  const { onClose, uploader } = props
  const { hasUnsavedChanges, draftIdea } = useIdeaComposerState()

  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const mergedRef = useMergeRefs(ref, wrapperRef)

  const container = document.getElementById('portals-container')

  const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] =
    React.useState(false)

  const { ideas: ideasLimit } = useOrganizationLimits() ?? {}
  const { ideasCount } = useIdeasCount()
  const isIdeasLimitReached = ideasLimit && ideasCount >= ideasLimit
  const location = useLocation()
  const locationState = isIdeaComposerLocationState(location.state)
    ? location.state
    : undefined
  const isEditAction = locationState?.action === 'edit'

  /**
   * Check for unsaved changes before closing the composer.
   */
  const handleClose = React.useCallback(
    (event?: React.MouseEvent | KeyboardEvent) => {
      event?.stopPropagation()
      event?.preventDefault()
      if (hasUnsavedChanges()) {
        setIsDiscardChangesDialogOpen(true)
      } else {
        onClose()
      }
    },
    [hasUnsavedChanges, onClose],
  )

  /**
   * Overlay click handler - if the click is outside the dialog content, close the composer.
   * Due to the custom overlay setup, clicks inside the composer can sometimes trigger this event.
   */
  const onOverlayClick = (event: React.MouseEvent | KeyboardEvent): void => {
    const target = event.target
    const wrapper = wrapperRef.current
    if (target instanceof Node && wrapper?.contains(target)) return

    handleClose(event)
  }

  // Prevent event from propagating or triggering.
  // Used to prevent interactions outside the dialog content so we can manage it ourselves.
  // This is necessary because certain interactions seem to bypass our change checks.
  // E.g., opening Canva modal and clicking its close button will incorrectly trigger
  // Dialog.Content's onInteractOutside event and close the composer without change detection.
  const blockEvent = React.useCallback(
    (
      e:
        | CustomEvent<{ originalEvent: PointerEvent }>
        | CustomEvent<{ originalEvent: FocusEvent }>
        | React.MouseEvent,
    ) => {
      e.stopPropagation()
      e.preventDefault()
    },
    [],
  )

  if (isIdeasLimitReached && !isEditAction) {
    return <IdeasLimitReachedModal open={true} onClose={onClose} />
  }

  return (
    <>
      <Dialog.Portal container={container}>
        {/*
        This component is a div instead of Dialog.Overlay
        Context: https://github.com/radix-ui/primitives/issues/1159
      */}
        {/* disabling eslint rules because overlay doesn't need key listeners */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className={styles.overlay} onClick={onOverlayClick} />
        <Dialog.Content
          id="ideas-content-wrapper"
          className={styles.content}
          onInteractOutside={blockEvent}
          onEscapeKeyDown={handleClose}
          ref={mergedRef}
          aria-describedby={undefined}
        >
          <VisuallyHidden>
            <Dialog.Title>
              {draftIdea?.id
                ? draftIdea.content.title
                  ? `Editing idea: ${draftIdea.content.title}`
                  : 'Editing idea'
                : 'Creating idea'}
            </Dialog.Title>
          </VisuallyHidden>
          <Flex
            className={styles.contentWithSidepanel}
            direction="row"
            justify="start"
            align="center"
            gap="xs"
          >
            {/* AI ASSISTANT SIDE PANEL */}
            <AIAssistantIdeasSidePanel>
              <IdeaEditor container={container} uploader={uploader} />
            </AIAssistantIdeasSidePanel>
          </Flex>

          <Dialog.Close asChild>
            <UnstyledButton
              className={styles.closeButton}
              onClick={handleClose}
            >
              <VisuallyHidden>Close</VisuallyHidden>
              <XIcon
                className={styles.closeIcon}
                color="inherit"
                size="medium"
              />
            </UnstyledButton>
          </Dialog.Close>

          {/* Discard changes confirmation dialog */}
          <ConfirmCloseModal
            open={isDiscardChangesDialogOpen}
            onOpenChange={setIsDiscardChangesDialogOpen}
            onCancel={(event: React.MouseEvent): void => {
              event.preventDefault()
              event.stopPropagation()
              setIsDiscardChangesDialogOpen(false)
            }}
            onConfirmClose={(): void => {
              setIsDiscardChangesDialogOpen(false)
              onClose()
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </>
  )
})

IdeaComposerContent.displayName = 'IdeaComposerContent'

type DiffableIdea = {
  title?: string
  text?: string
  media?: string[]
  tags?: string[]
  groupId?: string
}

/**
 * Flatten idea into a object that's easy to compare for changes.
 * This is used to determine if the idea has unsaved changes.
 * Only include properties that should be watched.
 *
 * E.g. 'aiAssisted' property shouldn't prevent closing if it's the only change detected.
 */
const getDiffableIdea = (idea: DraftIdea): DiffableIdea => ({
  title: idea.content.title?.trim() ?? '',
  text: idea.content.text?.trim() ?? '',
  media: idea.content.media?.map((el) => el.url ?? '') ?? [],
  tags: idea.content.tags?.map((el) => el.id) ?? [],
  groupId: idea.groupId?.trim() ?? UNASSIGNED_GROUP_ID,
})

/**
 * Empty idea used as a default value for the composer if no initialIdea is provided.
 */
const emptyIdea: DraftIdea = {
  content: {
    title: '',
    text: '',
    media: [],
  },
}

export type IdeaComposerContextValue = {
  draftIdea?: DraftIdea
  error: SaveIdeaError | null
  isLoading: boolean
  hasUnsavedChanges: () => boolean
  editor: BufferEditor
  setGroupId: (groupId: string | undefined) => void
  setTitle: (title: string) => void
  setText: (text: string) => void
  setSelectedTags: (tags: SelectedTag[]) => void
  createPostFromIdea: () => void
  saveDraftIdea: () => void
  closeComposer: () => void
}
const IdeaComposerContext = React.createContext<
  IdeaComposerContextValue | undefined
>(undefined)

type IdeaComposerProps = {
  initialIdea?: DraftIdea
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  uploader: Uploader
  /**
   * How the composer presents itself: a portaled dialog (default) or an
   * inline side panel rendered in place so the page can lay it out next to
   * its content.
   */
  variant?: 'dialog' | 'panel'
  /**
   * Panel variant only: called after the panel's exit animation finishes.
   * Route-driven owners must navigate here instead of on close, or the
   * panel unmounts before it can animate out.
   */
  onExited?: () => void
}

/**
 * Main composer component for creating and editing ideas.
 * This root component takes an initial idea that defaults to an empty idea,
 * manages controllable open state, and sets up the context for the composer.
 *
 * It manages all of the state related to the idea being edited and provides
 * actions for editing it, as well as preloading existing data, setting up the editor,
 * and passing down open state callbacks.
 *
 * It renders the IdeaComposerContext.Provider, the Dialog.Root, and the IdeaComposerContent.
 * Slate edits are synced back to `draftIdea.content.text`.
 * Media is managed separately in the Redux store and is selected from there when needed.
 * It does not seem worth the complexity to keep state in sync between Redux and IdeaComposer state.
 *
 * @example
 * ```tsx
 * <IdeaComposer
 *   initialIdea={{ content: { title: '', text: '', media: [] } }}
 *   defaultOpen={true}
 *   onOpenChange={(isOpen) => console.log('Composer open state:', isOpen)}
 * />
 * ```
 */
export const IdeaComposer = React.forwardRef<HTMLDivElement, IdeaComposerProps>(
  (props, ref): JSX.Element => {
    const {
      initialIdea = emptyIdea,
      uploader,
      variant = 'dialog',
      onExited,
    } = props

    /** PRIMARY STATE */
    const [open, setOpen] = useControllableState<boolean>({
      prop: props.open,
      defaultProp: props.defaultOpen,
      onChange: props.onOpenChange,
    })

    // Composer ID used as a key to reset the context when the composer is closed
    const composerId = React.useId()
    const dispatch = useAppDispatch()

    // Keep original idea for comparison
    const originalIdea = React.useRef<DraftIdea | undefined>(initialIdea)
    const [draftIdea, setDraftIdea] = React.useState<DraftIdea>(initialIdea)

    /**
     * LOAD MEDIA
     *
     * This should only be run once at initialization
     */
    React.useEffect(() => {
      if (
        isExistingIdea(initialIdea) &&
        initialIdea.content.media &&
        initialIdea.content.media?.length
      ) {
        dispatch(
          preloadMedia({
            media: initialIdea.content.media || [],
            uploaderId: IDEAS_UPLOADER_ID,
          }),
        )
      }
      // Disabling exhaustive-deps here because we only want this to run once
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * PLATE EDITOR INITIALIZATION
     *
     * This ensures the editor is only created once the composer is opened
     * and is destroyed when the composer is closed
     */
    const editorRef = React.useRef<BufferEditor>(createIdeaEditor())
    const editor = editorRef.current
    const initialTextRef = React.useRef(draftIdea.content.text)

    // When intitializing, if the draftIdea received text from initialIdea
    // parse the text to Slate state
    React.useEffect(() => {
      const initialText = initialTextRef.current
      if (initialText) {
        SlateJsProxy.createStateFromText(editor, initialText)
      }
    }, [editor])

    const draftIdeaMedia = useAppSelector((state) =>
      selectIncludedMedia(state, IDEAS_UPLOADER_ID),
    )

    const hasUnsavedChanges = React.useCallback(() => {
      if (!draftIdea || !originalIdea.current) return false
      const currentIdea = {
        ...draftIdea,
        content: {
          ...draftIdea.content,
          media: draftIdeaMedia,
          text: getPlainText(editor),
        },
      }
      return !isEqual(
        getDiffableIdea(currentIdea),
        getDiffableIdea(originalIdea.current),
      )
    }, [draftIdea, draftIdeaMedia, editor])

    /**
     * IDEA COMPOSER ACTIONS AND SETTERS
     */
    const closeComposer = React.useCallback(() => {
      setOpen(false)
    }, [setOpen])

    const { saveIdea, isLoading, error } = useSaveIdea({
      onCompleted: closeComposer,
    })

    const saveDraftIdea = React.useCallback(() => {
      saveIdea({
        ...draftIdea,
        content: {
          ...draftIdea.content,
          // Include media from Redux state
          media: draftIdeaMedia,
        },
      })
    }, [draftIdea, draftIdeaMedia, saveIdea])

    const setGroupId = React.useCallback(
      (groupId: string | undefined) => {
        setDraftIdea((current) => ({ ...current, groupId }))
      },
      [setDraftIdea],
    )

    const setTitle = React.useCallback(
      (title: string) => {
        setDraftIdea((current) => ({
          ...current,
          content: { ...current.content, title },
        }))
      },
      [setDraftIdea],
    )

    const setText = React.useCallback(
      (text: string) => {
        setDraftIdea((current) => {
          if (current?.content?.text !== text) {
            return {
              ...current,
              content: { ...current.content, text },
            }
          }
          return current
        })
      },
      [setDraftIdea],
    )

    const setSelectedTags = React.useCallback(
      (tags: SelectedTag[]) => {
        setDraftIdea((current) => ({
          ...current,
          content: { ...current.content, tags },
        }))
      },
      [setDraftIdea],
    )

    /**
     * CREATE POST FROM IDEA
     */
    const accountId = useAccountId()
    const accountEmail = useAccountEmail()
    const createPostFromIdea = React.useCallback(() => {
      if (!accountId || !accountEmail) return
      dispatch(
        createPostFromIdeaThunk({
          idea: {
            ...draftIdea,
            content: {
              ...draftIdea.content,
              // Include media from Redux state
              media: draftIdeaMedia,
            },
          },
          user: { id: accountId, email: accountEmail },
        }),
      )
      closeComposer()
    }, [
      closeComposer,
      dispatch,
      draftIdea,
      draftIdeaMedia,
      accountId,
      accountEmail,
    ])

    const contextValue = React.useMemo(
      () => ({
        saveDraftIdea,
        editor,
        error,
        isLoading,
        hasUnsavedChanges,
        draftIdea,
        setGroupId,
        setTitle,
        setText,
        setSelectedTags,
        createPostFromIdea,
        closeComposer,
      }),
      [
        saveDraftIdea,
        editor,
        error,
        isLoading,
        hasUnsavedChanges,
        draftIdea,
        setGroupId,
        setTitle,
        setText,
        setSelectedTags,
        createPostFromIdea,
        closeComposer,
      ],
    )

    return (
      <IdeaComposerContext.Provider key={composerId} value={contextValue}>
        {variant === 'panel' ? (
          <AnimatePresence onExitComplete={onExited}>
            {open ? (
              <IdeaComposerPanel onClose={closeComposer} uploader={uploader} />
            ) : null}
          </AnimatePresence>
        ) : (
          <Dialog.Root open={open} modal={false} onOpenChange={setOpen}>
            <IdeaComposerContent
              onClose={closeComposer}
              ref={ref}
              uploader={uploader}
            />
          </Dialog.Root>
        )}
      </IdeaComposerContext.Provider>
    )
  },
)

IdeaComposer.displayName = 'IdeaComposer'

export const useIdeaComposerState = (): IdeaComposerContextValue => {
  const context = React.useContext(IdeaComposerContext)
  if (!context) {
    throw new Error(
      'useIdeaComposer must be used within an IdeaComposerProvider',
    )
  }
  return context
}

export const useIdeaComposerEditor = (): BufferEditor => {
  const { editor } = useIdeaComposerState()
  return editor
}
